<template>
  <div v-if="asset" class="al">
    <!-- Hero -->
    <div class="al-hero">
      <img v-if="image" :src="image" :alt="asset.name" class="al-hero-img" @error="onImageError" />
      <img v-else class="al-hero-img" :src="noImageAvailable" alt="No image available" />
      <div class="al-hero-scrim"></div>

      <div class="al-hero-top">
        <button type="button" class="al-pill" @click="onChangeAsset">
          <v-icon size="20">mdi-chevron-left</v-icon><span>Assets</span>
        </button>
      </div>

      <div class="al-hero-title">
        <span class="al-hero-meta">{{ metaLine }}</span>
        <h1 class="al-hero-name">{{ asset.name }}</h1>
      </div>
    </div>

    <!-- Body -->
    <div class="al-body">
      <!-- Stat strip -->
      <div class="al-stats">
        <div class="al-stat">
          <span class="al-stat-num pending">{{ statsLoading ? '—' : num(pending) }}</span>
          <span class="al-stat-lbl">Pending</span>
        </div>
        <div class="al-stat">
          <span class="al-stat-num">{{ statsLoading ? '—' : num(markers) }}</span>
          <span class="al-stat-lbl">Markers</span>
        </div>
        <div class="al-stat">
          <span class="al-stat-num small">{{ lastWalkdown }}</span>
          <span class="al-stat-lbl">Last walkdown</span>
        </div>
      </div>

      <!-- Actions (stacked, single column) -->
      <!-- Open 3D — mirrors AssetDetailPanel: model-gated only (no permission gate), disabled until ready. -->
      <button type="button" class="al-primary"
        :class="{ disabled: open3dDisabled }" @click="open3dDisabled ? null : go('AssetViewer')">
        <v-icon size="20">mdi-cube-outline</v-icon><span>Open 3D model</span>
      </button>
      <p v-if="open3dDisabled" class="al-hint">Model still processing…</p>

      <!-- Manage findings — mirrors AssetDetailPanel anomalyDisabled: model-gated + AnomalyView. -->
      <button v-if="can(Permissions.AnomalyView)" type="button" class="al-outline al-full al-manage"
        :disabled="anomalyDisabled" @click="anomalyDisabled ? null : goReview()">
        <v-icon size="18">mdi-clipboard-pulse-outline</v-icon><span>Manage findings</span>
        <span v-if="pending > 0" class="al-count">{{ pending > 99 ? '99+' : pending }}</span>
      </button>
      <!-- Asset admin — mirrors AssetDetailPanel adminDisabled: permission-only, never model-gated. -->
      <button v-if="can(Permissions.AssetAdminView)" type="button" class="al-outline al-full" @click="go('AssetAdmin')">
        <v-icon size="18">mdi-cog-outline</v-icon><span>Asset admin</span>
      </button>
      <!-- Report — mirrors AssetDetailPanel: shown for AnomalyView, disabled until model ready. -->
      <button v-if="can(Permissions.AnomalyView)" type="button" class="al-outline al-full"
        :disabled="reportDisabled" @click="reportDisabled ? null : goReport()">
        <v-icon size="18">mdi-file-chart-outline</v-icon><span>Report</span>
      </button>
    </div>
  </div>

  <div v-else class="al-empty">
    <v-icon size="48" color="#4a5a60">mdi-package-variant</v-icon>
    <p>No asset selected.</p>
    <button type="button" class="al-outline" @click="go('Assets')">Back to assets</button>
  </div>
</template>

<script>
import { useAssetStore } from '@/store/assetStore';
import ModelVersionService from '@/services/ModelVersionService';
import DashboardDataService from '@/services/DashboardDataService';
import organizationSasService from '@/services/OrganizationSasService';
import { Permissions } from '@/constants/permissions';
import { usePermissions } from '@/composable/usePermissions';
import { SCAN_PROCESSING_SUCCESS } from '@/utils/ScanStatus';
import noImageAvailable from '@/assets/no-image-available.svg';
import { useDisplay } from 'vuetify';
import { useRouter } from 'vue-router';

export default {
  name: 'AssetLandingView',
  setup() {
    const { can } = usePermissions();
    const { mdAndUp } = useDisplay();
    const router = useRouter();
    return { can, Permissions, mdAndUp, router };
  },
  data() {
    return {
      noImageAvailable,
      assetStore: null,
      asset: null,
      image: null,
      summary: null,
      statsLoading: true,
      version: null,
      lastScanDate: null,
      actionsDisabled: true,
      frameFallbackTried: false,
    };
  },
  computed: {
    pending() { return this.summary ? Number(this.summary.totalAnomaliesPending || 0) : 0; },
    total() { return this.summary ? Number(this.summary.totalAnomalies || 0) : 0; },
    markers() { return this.total; },
    open3dDisabled() { return this.actionsDisabled; },
    anomalyDisabled() { return this.actionsDisabled || !this.can(Permissions.AnomalyView); },
    reportDisabled() { return this.actionsDisabled || !this.can(Permissions.AnomalyView); },
    reviewed() { return Math.max(0, this.total - this.pending); },
    reviewedPct() { return this.total ? Math.round((this.reviewed / this.total) * 100) : 0; },
    metaLine() {
      const parts = [];
      if (this.version?.versionNumber != null) parts.push(`V${this.version.versionNumber}`);
      const scanCount = this.version?.scans?.length || (this.version?.associatedScan ? 1 : 0);
      if (scanCount) parts.push(`${scanCount} ${scanCount === 1 ? 'scan' : 'scans'}`);
      if (this.version?.createdBy) parts.push(this.version.createdBy);
      return parts.join(' · ') || 'No scans yet';
    },
    lastWalkdown() {
      if (!this.lastScanDate) return '—';
      const d = new Date(this.lastScanDate);
      return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: '2-digit' });
    },
  },
  watch: {
    // This is the mobile-only post-selection screen. If the viewport crosses to desktop width
    // (e.g. a tablet rotated to landscape >=960), hand back to the assets list so the desktop
    // right-hand panel layout takes over instead of leaking the full-bleed phone layout.
    mdAndUp(isDesktop) {
      if (isDesktop) this.router.replace({ name: 'Assets' });
    },
  },
  async mounted() {
    if (this.mdAndUp) { this.router.replace({ name: 'Assets' }); return; }
    this.assetStore = useAssetStore();
    this.asset = this.assetStore.asset;
    if (!this.asset?.id) { this.$router.replace({ name: 'Assets' }); return; }

    this.image = await organizationSasService.getSignedUrl(this.asset.imagePath).catch(() => null);
    await Promise.allSettled([this.loadSummary(), this.loadVersionAndGate(), this.loadScans()]);
    // No overview image → fall back to the current scan's first frame (same as the asset card).
    if (!this.image) await this.loadFrameFallback();
  },
  methods: {
    async loadSummary() {
      try {
        this.summary = await DashboardDataService.getModelVersionSummaryById(this.asset.id);
      } catch {
        this.summary = null;
      } finally {
        this.statsLoading = false;
      }
    },
    async loadScans() {
      try {
        const scans = await ModelVersionService.getScansByAssetId(this.asset.id);
        this.lastScanDate = (scans || []).reduce((latest, s) => {
          const t = new Date(s.createdOn || s.statusUpdatedOn || 0).getTime();
          return t > latest.t ? { t, iso: s.createdOn || s.statusUpdatedOn } : latest;
        }, { t: 0, iso: null }).iso;
      } catch { /* leave — */ }
    },
    // Mirrors the desktop gate: model/anomaly actions enabled only when a current version's scans
    // have all processed (Duallo admins bypass; no version = gated for everyone).
    async loadVersionAndGate() {
      try {
        const mv = await ModelVersionService.getCurrentModelVersion(this.asset.id);
        this.version = mv;
        if (!mv) { this.actionsDisabled = true; return; }
        const versionScanIds = (mv.scans?.length ? mv.scans.map(s => s.scanId) : [mv.associatedScan]).filter(Boolean);
        const allScans = await ModelVersionService.getScansByAssetId(this.asset.id);
        const statusById = new Map((allScans || []).map(s => [s.id, s.status]));
        const allProcessed = versionScanIds.length > 0
          && versionScanIds.every(id => statusById.get(id) === SCAN_PROCESSING_SUCCESS);
        this.actionsDisabled = !allProcessed && !this.can(Permissions.DualloAdminView);
      } catch {
        this.actionsDisabled = true;
      }
    },
    num(n) { return n == null ? '0' : Number(n).toLocaleString(); },
    // Best-effort hero fallback: first webp frame of the current scan (mirrors the asset card).
    async loadFrameFallback() {
      if (this.frameFallbackTried) return;
      this.frameFallbackTried = true;
      try {
        const mv = this.version || await ModelVersionService.getCurrentModelVersion(this.asset.id);
        const scanId = mv?.associatedScan || mv?.scans?.find(s => s.scanId)?.scanId;
        if (!scanId) return;
        const frames = await ModelVersionService.getFramesByScanId(scanId);
        const first = (frames || []).find(f => f.imagePath);
        if (first) this.image = await organizationSasService.getSignedUrl(first.imagePath);
      } catch { /* leave placeholder */ }
    },
    onImageError() { this.image = null; this.loadFrameFallback(); },
    onChangeAsset() {
      this.assetStore.setAsset(null);
      this.assetStore.clearAsset();
      this.$router.push({ name: 'Assets' });
    },
    go(name) { this.$router.push({ name, params: { id: this.asset.id } }); },
    goStreet() {
      if (this.actionsDisabled) return;
      this.$router.push({ name: 'AssetViewer', params: { id: this.asset.id }, query: { view: 'street' } });
    },
    goReport() {
      this.$router.push({ name: 'AnomalyManagement', params: { id: this.asset.id }, query: { report: '1' } });
    },
    goReview() {
      this.$router.push({ name: 'AnomalyManagement', params: { id: this.asset.id } });
    },
    goUpload() {
      this.$router.push({ name: 'AssetAdmin', params: { id: this.asset.id }, query: { tab: 'scans' } });
    },
  },
};
</script>

<style scoped>
/* Full-bleed hero: cancel the scroll container's side + top padding (incl. mobile safe-area). */
.al { display: flex; flex-direction: column; color: #eef3f2; margin: calc(-20px - env(safe-area-inset-top, 0px)) -20px 0; }

/* Hero */
.al-hero { position: relative; height: 260px; overflow: hidden; }
.al-hero-img { width: 100%; height: 100%; object-fit: cover; object-position: 30% 45%; display: block; }
.al-hero-scrim { position: absolute; inset: 0; background: linear-gradient(to top, rgba(14,20,24,0.96) 0%, rgba(14,20,24,0.4) 55%, rgba(14,20,24,0.2) 100%); }
.al-hero-top {
  position: absolute; top: calc(14px + env(safe-area-inset-top, 0px)); left: 12px; right: 12px;
  display: flex; align-items: center; justify-content: space-between;
}
.al-pill {
  height: 40px; padding: 0 14px 0 8px; border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.14); background: rgba(18, 25, 30, 0.85);
  color: #eef3f2; display: inline-flex; align-items: center; gap: 2px; font: inherit; font-size: 0.9rem; font-weight: 600;
  cursor: pointer; backdrop-filter: blur(4px);
}
.al-pill-icon { width: 40px; padding: 0; justify-content: center; }
.al-hero-title { position: absolute; left: 16px; right: 16px; bottom: 16px; display: flex; flex-direction: column; gap: 4px; }
.al-hero-meta { font-size: 0.8rem; color: #c9d4d7; }
.al-hero-name { font-size: 1.6rem; font-weight: 700; line-height: 1.2; margin: 0; color: #fff; }

/* Body */
.al-body { padding: 14px 16px; display: flex; flex-direction: column; gap: 12px; }

.al-card {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
}

.al-stats {
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr));
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 16px; overflow: hidden;
}
.al-stat { padding: 10px 14px; display: flex; flex-direction: column; border-left: 1px solid rgba(255, 255, 255, 0.06); min-width: 0; }
.al-stat:first-child { border-left: none; }
.al-stat-num { font-size: 1.35rem; font-weight: 700; color: var(--duallo-accent-muted); line-height: 1; font-variant-numeric: tabular-nums; }
.al-stat-num.pending { color: #f0a623; }
.al-stat-num.small { font-size: 0.95rem; color: #e6edf0; line-height: 1.3; }
.al-stat-lbl { margin-top: 4px; font-size: 0.72rem; color: #8aa0aa; }

.al-primary {
  height: 52px; border-radius: 14px; border: none;
  background: var(--duallo-accent-gradient); color: #071514;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  font: inherit; font-size: 1rem; font-weight: 700; cursor: pointer; width: 100%;
}
.al-primary.disabled { opacity: 0.5; cursor: not-allowed; }

.al-dual { display: flex; gap: 10px; }
.al-outline {
  flex: 1 1 0; height: 48px; border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.2); background: transparent; color: #eef3f2;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  font: inherit; font-size: 0.92rem; font-weight: 600; cursor: pointer;
}
.al-outline:hover:not(:disabled) { background: rgba(255, 255, 255, 0.05); }
.al-outline:disabled { opacity: 0.45; cursor: not-allowed; }
.al-full { flex: none; width: 100%; }

/* Manage findings — amber accent (the key review action) + pending count pill. */
.al-manage { border-color: rgba(240, 166, 35, 0.4); background: rgba(240, 166, 35, 0.06); }
.al-manage:hover { background: rgba(240, 166, 35, 0.12); }
.al-count {
  min-width: 22px; height: 22px; padding: 0 7px; border-radius: 999px;
  background: #f0a623; color: #241700; font-size: 0.78rem; font-weight: 800; line-height: 22px;
}

/* Inline "still processing" hint under a gated Open 3D button (tooltips are unreliable on touch). */
.al-hint { margin: -4px 0 0; font-size: 0.8rem; color: #8aa0aa; text-align: center; }

/* ── Action cards (match the desktop asset card), stacked one per row on mobile ── */
.action-row { display: flex; flex-direction: column; gap: 10px; }
.card {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 16px;
  min-width: 0;
}
.action-card {
  position: relative;
  display: flex; flex-direction: column; align-items: flex-start; gap: 12px;
  width: 100%; text-align: left; font: inherit; cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.action-badge {
  position: absolute; top: 10px; right: 10px;
  min-width: 22px; height: 22px; padding: 0 6px;
  display: inline-flex; align-items: center; justify-content: center; border-radius: 999px;
  background: #f0a623; color: #241700; font-size: 0.72rem; font-weight: 800; line-height: 1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.45);
}
.action-card:not(.disabled):hover { border-color: rgba(122, 162, 255, 0.4); background: rgba(122, 162, 255, 0.05); }
.action-card.disabled { opacity: 0.5; cursor: not-allowed; }
.action-card.disabled .action-icon { background: rgba(255, 255, 255, 0.05); }
.action-card.disabled .action-arrow { color: #5a6970; }
.action-icon { width: 38px; height: 38px; border-radius: 10px; background: rgba(122, 162, 255, 0.12); display: flex; align-items: center; justify-content: center; }
.action-head { display: flex; align-items: center; gap: 8px; width: 100%; }
.action-title { font-size: 1.05rem; font-weight: 700; color: #fff; min-width: 0; overflow: hidden; text-overflow: ellipsis; }
.action-arrow { margin-left: auto; color: #7aa2ff; transition: transform 0.15s ease; flex: 0 0 auto; }
.action-card:not(.disabled):hover .action-arrow { transform: translateX(3px); }

.open3d-card:not(.disabled):hover { border-color: rgba(100, 255, 218, 0.4); background: rgba(100, 255, 218, 0.05); }
.open3d-icon { background: rgba(100, 255, 218, 0.12); }
.open3d-arrow { color: var(--duallo-accent); }

.anomaly-card:not(.disabled):hover { border-color: rgba(240, 181, 74, 0.4); background: rgba(240, 181, 74, 0.05); }
.anomaly-icon { background: rgba(240, 181, 74, 0.12); }
.anomaly-arrow { color: #f0b54a; }

.admin-card:not(.disabled):hover { border-color: rgba(199, 146, 234, 0.4); background: rgba(199, 146, 234, 0.05); }
.admin-icon { background: rgba(199, 146, 234, 0.12); }
.admin-arrow { color: #c792ea; }

.report-cta {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; padding: 11px; border-radius: 12px;
  background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.1);
  color: #cfd8dc; font: inherit; font-size: 0.9rem; font-weight: 600; cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.report-cta:not(.disabled):hover { background: rgba(102, 199, 180, 0.08); border-color: rgba(102, 199, 180, 0.4); color: #fff; }
.report-cta.disabled { opacity: 0.5; cursor: not-allowed; }

.al-findings { padding: 14px; display: flex; flex-direction: column; gap: 10px; }
.al-findings-head { display: flex; align-items: baseline; gap: 8px; }
.al-findings-num { font-size: 1.9rem; font-weight: 700; color: #f0a623; line-height: 1; }
.al-findings-cap { font-size: 0.88rem; color: #c9d4d7; }
.al-findings-bar { display: flex; height: 6px; border-radius: 3px; overflow: hidden; gap: 2px; }
.al-findings-bar .seg-done { background: #4ade80; }
.al-findings-bar .seg-pending { flex: 1 1 auto; background: #f0a623; }
.al-findings-meta { font-size: 0.78rem; color: #8aa0aa; }
.al-findings-btn {
  height: 44px; border-radius: 12px; border: 1px solid rgba(240, 166, 35, 0.5);
  background: rgba(240, 166, 35, 0.1); color: #f0a623; font: inherit; font-size: 0.92rem; font-weight: 600; cursor: pointer;
}

.al-upload { display: flex; align-items: center; gap: 12px; padding: 12px 14px; text-align: left; cursor: pointer; color: #eef3f2; }
.al-upload-icon { width: 36px; height: 36px; border-radius: 10px; background: rgba(100, 255, 218, 0.12); display: flex; align-items: center; justify-content: center; flex: 0 0 auto; }
.al-upload-text { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
.al-upload-title { font-size: 0.95rem; font-weight: 600; }
.al-upload-sub { font-size: 0.78rem; color: #8aa0aa; }

.al-empty {
  min-height: 60vh; display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 12px; color: #8aa0aa; text-align: center;
}
</style>
